import { Refresh } from '@mui/icons-material';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    Button,
    Paper,
    List,
    Grid,
    GridProps,
    PaperProps,
    CircularProgress,
} from '@mui/material'
import React, { Dispatch, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next';
import * as certificateService from 'services/certificate-service';

interface IProps {
    renews: { id: string, reference: string, name: string }[];
    open: boolean;
    close: Dispatch<SetStateAction<boolean>>;
    refresh: () => void;
}

const RenewBulkModal = (props: IProps) => {
    const { t } = useTranslation();
    const { renews, open, close, refresh } = props;
    const [validated, setValidated] = React.useState<boolean>(false);
    const [validatedRenewals, setValidatedRenewals] = React.useState<{ id: string, reference: string, name: string }[]>([]);
    const [unvalidatedRenewals, setUnvalidatedRenewals] = React.useState<{ id: string, reference: string, name: string }[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);

    const handleClose = (): void => {
        setValidated(false);
        refresh();
        close(false);
    }

    const handleValidate = (): void => {
        setLoading(true);
        certificateService.validateRenewals(renews.map((renew) => renew.id)).then((res: string[]) => {
            const tempValidatedRenewals = renews.filter((renew) => res.includes(renew.id));
            certificateService.renewCertificatesList(tempValidatedRenewals.map((renew) => renew.id)).then((res: ApplicationDetailDTO[]) => {
                setValidated(true);
                setValidatedRenewals(tempValidatedRenewals);
                setLoading(false);
            });
            setUnvalidatedRenewals(renews.filter((renew) => !res.includes(renew.id)));
        });
    }

    const paperProps: PaperProps = {
        sx: {
            maxHeight: 200,
            overflow: 'auto',
            marginTop: '1rem',
            marginBottom: '1rem',
            border: '1px solid #ccc',
            ':last-child': {
                paddingBottom: '0.50rem',
            }
        },
        elevation: 0,
    }

    return (
        <Dialog fullWidth={true} maxWidth={'sm'} open={open} onClose={handleClose}>
            <DialogTitle>
                {t('renew.bulkTitle')}
            </DialogTitle>
            {!loading &&
                <DialogContent>
                    {!validated &&
                        <Box>
                            <Typography variant='body1'>
                                {t('renew.bulkDescription')}
                            </Typography>
                            <Paper {...paperProps}>
                                {renews.map((renew) => (
                                    <Box key={renew.id} sx={{ display: 'flex', paddingTop: '0.50rem' }}>
                                        <Box sx={{ marginLeft: '1.25rem', width: '25%' }}>
                                            {renew.reference}
                                        </Box>
                                        <Box sx={{}}>
                                            {renew.name}
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>
                        </Box>
                    }
                    {validated &&
                        <Box>
                            {validatedRenewals.length > 0 &&
                                <Box>
                                    <Typography variant='body1'>
                                        {t('renew.success')}
                                    </Typography>
                                    <Paper {...paperProps}>
                                        {validatedRenewals.map((renew) => (
                                            <Box key={renew.id} sx={{ display: 'flex', paddingTop: '0.50rem' }}>
                                                <Box sx={{ marginLeft: '1.25rem', width: '25%' }}>
                                                    {renew.reference}
                                                </Box>
                                                <Box sx={{}}>
                                                    {renew.name}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Paper>
                                </Box>
                            }
                            {unvalidatedRenewals.length > 0 &&
                                <Box>
                                    <Typography variant='body1'>
                                        {t('renew.unvalidated')}
                                    </Typography>
                                    <Paper {...paperProps}>
                                        {unvalidatedRenewals.map((renew) => (
                                            <Box key={renew.id} sx={{ display: 'flex', paddingTop: '0.50rem' }}>
                                                <Box sx={{ marginLeft: '1.25rem', width: '25%' }}>
                                                    {renew.reference}
                                                </Box>
                                                <Box sx={{}}>
                                                    {renew.name}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Paper>
                                </Box>
                            }
                        </Box>
                    }
                </DialogContent>
            }
            {loading &&
                <DialogContent sx={{
                    display: 'flex',
                    alignContent: 'center',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <CircularProgress />
                </DialogContent>
            }
            <DialogActions>
                {!validated && !loading &&
                    <Button variant='contained' onClick={handleClose} color="primary">{t('buttons.cancel')}</Button>
                }
                {!validated && !loading &&
                    <Button variant='contained' onClick={handleValidate} color="success">{t('renew.validate')}</Button>
                }
                {validated && !loading &&
                    <Button variant='contained' onClick={handleClose} color="success">OK</Button>
                }
            </DialogActions>
        </Dialog>
    )
};

export default RenewBulkModal;